import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Printer, 
  User,
  FileText,
  Shield,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useLifely } from '../context/LifelyContext';
import { toast } from 'sonner';

// Printable document component
const PrintableDocument = ({ contact, selectedMapsList, includeWillStatus, includeWishes, redactSensitive, willStatus, getMapNotes, getEntriesForMap, getMapWishes, onBack, onPrint }) => {
  const redactText = (text) => {
    if (!redactSensitive || !text) return text;
    return text.replace(/(\d{4,})/g, '****');
  };

  return (
    <div className="print-bundle" data-testid="contact-printable-bundle">
      <div className="no-print mb-8 flex items-center gap-4">
        <Button onClick={onPrint}><Printer className="w-4 h-4 mr-2" />Print / Save as PDF</Button>
        <Button variant="ghost" onClick={onBack}>Back to Selection</Button>
      </div>
      <div className="space-y-8">
        <header className="border-b-2 border-border pb-6">
          <h1 className="text-2xl font-heading font-semibold">Information Summary for {contact.name}</h1>
          <p className="text-sm text-muted-foreground mt-2">Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          {redactSensitive && <Badge variant="outline" className="mt-2 text-amber-600 border-amber-300"><Shield className="w-3 h-3 mr-1" />Sensitive details redacted</Badge>}
        </header>
        <section>
          <div className="p-4 bg-muted/30 rounded-lg border border-border">
            <p className="text-sm">Dear {contact.name},</p>
            <p className="text-sm mt-2">This document contains information prepared for you. Please keep it secure.</p>
          </div>
        </section>
        {includeWillStatus && willStatus && (
          <section>
            <h2 className="text-xl font-heading font-medium mb-4 pb-2 border-b border-border">Will & Legal Status</h2>
            <p className="text-sm"><span className="text-muted-foreground">Status:</span> {willStatus.willExists || 'Not specified'}</p>
          </section>
        )}
        {selectedMapsList.map((map) => (
          <section key={map.id} className="page-break-before">
            <h2 className="text-xl font-heading font-medium mb-4 pb-2 border-b border-border">{map.name}</h2>
            {getMapNotes(map.id) && <p className="text-sm mb-4">{getMapNotes(map.id)}</p>}
            {includeWishes && getMapWishes(map.id)?.notes && (
              <div className="mb-4 p-3 bg-primary/5 rounded-lg"><p className="text-sm">{getMapWishes(map.id).notes}</p></div>
            )}
            {getEntriesForMap(map.id).length > 0 ? (
              <div className="space-y-3">
                {getEntriesForMap(map.id).map((entry) => (
                  <div key={entry.id} className="p-3 border border-border rounded-lg">
                    <p className="font-medium text-sm">{entry.label}</p>
                    <p className="text-sm text-muted-foreground">{redactText(entry.location)}</p>
                    {entry.firstAction && <p className="text-sm mt-2 text-primary"><strong>First action:</strong> {entry.firstAction}</p>}
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground italic">No entries.</p>}
          </section>
        ))}
        <footer className="pt-8 border-t border-border text-sm text-muted-foreground">
          <p>This document was prepared using Lifely for informational purposes only.</p>
        </footer>
      </div>
    </div>
  );
};

const ContactExport = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { 
    contacts, 
    maps, 
    getEntriesForMap, 
    getMapNotes, 
    getMapWishes, 
    willStatus, 
    shareSettings, 
    getContactById,
    fetchMaps,
    fetchContacts,
    fetchWillStatus,
    fetchSharing,
    isAuthenticated,
    loading
  } = useLifely();

  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch data when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && !dataLoaded) {
        try {
          await Promise.all([
            fetchMaps(),
            fetchContacts(),
            fetchWillStatus(),
            fetchSharing()
          ]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load contact export data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, dataLoaded, fetchMaps, fetchContacts, fetchWillStatus, fetchSharing]);

  const contact = getContactById(contactId);
  const [selectedMaps, setSelectedMaps] = useState({});
  const [includeWillStatus, setIncludeWillStatus] = useState(true);
  const [includeWishes, setIncludeWishes] = useState(true);
  const [redactSensitive, setRedactSensitive] = useState(true);
  const [showPrintable, setShowPrintable] = useState(false);

  const shareSettingsList = shareSettings || [];
  const hasShareRules = shareSettingsList.filter(rule => rule.contactId === contactId || rule.contact_id === contactId).length > 0;
  const handleToggleMap = (mapId) => setSelectedMaps(prev => ({ ...prev, [mapId]: !prev[mapId] }));
  const handleSelectAll = () => {
    const allSelected = Object.keys(selectedMaps).length === maps.length && Object.values(selectedMaps).every(v => v);
    if (allSelected) setSelectedMaps({});
    else {
      const newSelected = {};
      maps.forEach(m => { newSelected[m.id] = true; });
      setSelectedMaps(newSelected);
    }
  };
  const selectedCount = Object.values(selectedMaps).filter(Boolean).length;
  const hasAnySelection = selectedCount > 0 || includeWillStatus;
  const handleGenerateBundle = () => {
    if (!hasAnySelection) { toast.error('Please select at least one item'); return; }
    setShowPrintable(true);
  };

  // Show loading state
  const isLoading = loading.maps || loading.contacts || loading.willStatus;
  if (isLoading && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="space-y-6 animate-fade-in text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-lg font-medium">Contact not found</h2>
        <Link to="/contacts"><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />Back to Contacts</Button></Link>
      </div>
    );
  }

  if (showPrintable) {
    return (
      <PrintableDocument
        contact={contact}
        selectedMapsList={maps.filter(m => selectedMaps[m.id])}
        includeWillStatus={includeWillStatus}
        includeWishes={includeWishes}
        redactSensitive={redactSensitive}
        willStatus={willStatus}
        getMapNotes={getMapNotes}
        getEntriesForMap={getEntriesForMap}
        getMapWishes={getMapWishes}
        onBack={() => setShowPrintable(false)}
        onPrint={() => window.print()}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contact-export-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-8 px-2"><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-2xl font-heading font-semibold">Export for {contact.name}</h1>
          <p className="text-sm text-muted-foreground">Create a personalized information summary</p>
        </div>
      </div>

      <Card className="border border-border bg-muted/30">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><User className="w-6 h-6 text-primary" /></div>
          <div className="flex-1">
            <p className="font-medium">{contact.name}</p>
            <p className="text-sm text-muted-foreground capitalize">{contact.category}</p>
          </div>
          {hasShareRules && <Badge variant="outline" className="text-green-600 border-green-300"><Check className="w-3 h-3 mr-1" />Has sharing rules</Badge>}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base font-medium">Information to Include</CardTitle>
                <CardDescription className="text-xs">Select what {contact.name} should see</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={handleSelectAll}>{selectedCount === maps.length ? 'Deselect All' : 'Select All'}</Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {maps.map((map) => (
                  <div key={map.id} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${selectedMaps[map.id] ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/30'}`} onClick={() => handleToggleMap(map.id)}>
                    <div className="flex items-center gap-3">
                      <Checkbox checked={selectedMaps[map.id] || false} onCheckedChange={() => handleToggleMap(map.id)} />
                      <div>
                        <p className="font-medium text-sm">{map.name}</p>
                        <p className="text-xs text-muted-foreground">{getEntriesForMap(map.id).length} entries</p>
                      </div>
                    </div>
                    {selectedMaps[map.id] && <Check className="w-4 h-4 text-primary" />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardHeader className="pb-3"><CardTitle className="text-base font-medium">Additional Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <div><p className="font-medium text-sm">Will & Legal Status</p></div>
                </div>
                <Switch checked={includeWillStatus} onCheckedChange={setIncludeWillStatus} />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <div><p className="font-medium text-sm">Your Wishes</p></div>
                </div>
                <Switch checked={includeWishes} onCheckedChange={setIncludeWishes} />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border border-border">
            <CardHeader className="pb-3"><CardTitle className="text-base font-medium">Privacy Options</CardTitle></CardHeader>
            <CardContent>
              <div className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${redactSensitive ? 'border-amber-300 bg-amber-50/50' : 'border-border'}`} onClick={() => setRedactSensitive(!redactSensitive)}>
                <div className="flex items-center gap-3">
                  {redactSensitive ? <EyeOff className="w-5 h-5 text-amber-600" /> : <Eye className="w-5 h-5 text-muted-foreground" />}
                  <div><p className="font-medium text-sm">Redact Sensitive Details</p><p className="text-xs text-muted-foreground">Hide account numbers</p></div>
                </div>
                <Switch checked={redactSensitive} onCheckedChange={setRedactSensitive} />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border bg-muted/30">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Selected:</span>
                <span className="font-medium">{selectedCount} maps{includeWillStatus && ' + Will'}</span>
              </div>
              <Button onClick={handleGenerateBundle} disabled={!hasAnySelection} className="w-full">
                <Eye className="w-4 h-4 mr-2" />Preview for {contact.name.split(' ')[0]}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactExport;
